import { useCallback, useEffect, useState } from "react";
import { getServicesByOrganizationAsync } from "../api/ServiceApi";
import { Service } from "../models/ServiceModels";

interface UseServicesReturn {
  services: Service[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  addLocal: (service: Service) => void;
  updateLocal: (service: Service) => void;
  removeLocal: (id: string) => void;
}

export const useServices = (
  organizationId: string | undefined,
): UseServicesReturn => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!organizationId) {
      setServices([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await getServicesByOrganizationAsync(organizationId);
      if (res.success) {
        setServices(res.result ?? []);
        setError(null);
      } else {
        setError(res.message || "Failed to load services");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  const addLocal = useCallback((service: Service) => {
    setServices((prev) => [service, ...prev]);
  }, []);

  const updateLocal = useCallback((service: Service) => {
    setServices((prev) =>
      prev.map((item) => (item.id === service.id ? service : item)),
    );
  }, []);

  const removeLocal = useCallback((id: string) => {
    setServices((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return {
    services,
    loading,
    error,
    reload,
    addLocal,
    updateLocal,
    removeLocal,
  };
};
